import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dropdown, Menu, Modal, Space, Table, Tag } from "antd";
import dayjs from "dayjs";
import { CheckCircle, ChevronDown, Clock, Info, Loader2, PlayCircle } from "lucide-react";
import { useState } from 'react';
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { toast } from "react-toastify";
import { api } from "../../config/api";
import { useMyContext } from "../../context/MyContext";
import { getAdmins } from "../../lib/user";
import Loader from "../shared/loader/loader";


const statusOptions = [
  { value: "pending", label: "Pending", icon: Clock },
  { value: "In Progress", label: "In Progress", icon: PlayCircle },
  { value: "completed", label: "Completed", icon: CheckCircle },
];

const StatusDropdown = ({ status, onChange, id, loading }) => {
  console.log('status', status);
  const [isOpen, setIsOpen] = useState(false);

  const getStatusColor = (statusValue) => {
    const lower = statusValue?.toLowerCase();
    switch (lower) {
      case "in progress":
        return {
          backgroundColor: '#fff9e0',
          color: '#FFC93F',
          borderColor: '#FFC93F'
        };
      case "completed":
        return {
          backgroundColor: '#dcfce7',
          color: '#166534',
          borderColor: '#86efac'
        };
      case "pending":
        return {
          backgroundColor: '#dbeafe',
          color: '#1e40af',
          borderColor: '#93c5fd'
        };
      default:
        return {
          backgroundColor: '#f3f4f6',
          color: '#374151',
          borderColor: '#d1d5db'
        };
    }
  };

  const isLoading = loading === id

  const currentStatus = statusOptions.find(
    opt => opt.value.toLowerCase() === status?.toLowerCase()
  );
  const CurrentIcon = isLoading ? Loader2 : currentStatus?.icon || Info;

  const statusColors = getStatusColor(status);

  return (
    <div className="relative w-full max-w-xs">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-2 hover:shadow-md"
        style={{
          backgroundColor: isLoading ? "#fef3c7" : statusColors.backgroundColor,
          color: isLoading ? "#d97706" : statusColors.color,
          borderColor: isLoading ? "#d97706" : statusColors.borderColor
        }}
      >
        <div className="flex items-center gap-2">
          <CurrentIcon className={`${isLoading && 'animate-spin'}`} style={{ width: '16px', height: '16px' }} />
          <span className="text-sm font-medium capitalize truncate">
            {isLoading ? 'Please wait...' : status ? status : "Select Status"}
          </span>
        </div>
        <ChevronDown
          style={{
            width: '16px',
            height: '16px',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms'
          }}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 min-w-[250px] mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-80 overflow-y-auto">
            {statusOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = option.value.toLowerCase() === status?.toLowerCase();

              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(id, option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors 
                    } ${option === statusOptions[0] ? 'rounded-t-lg' : ''} ${option === statusOptions[statusOptions.length - 1] ? 'rounded-b-lg' : ''
                    }`}
                  style={{ backgroundColor: isSelected && '#dbeafe', color: isSelected && '#2563eb' }}
                >
                  {/* <Icon className={`w-4 h-4 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} /> */}
                  <Icon size={20} />
                  <span className={`text-sm font-medium whitespace-nowrap ${isSelected ? 'text-blue-600' : 'text-gray-700'}`}>
                    {option.label}
                  </span>
                  {isSelected && (
                    <CheckCircle className="w-4 h-4 ml-auto text-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};


const SuperAdminTodos = () => {
  const { user, token, isAuthenticated } = useMyContext();
  const [localTodos, setLocalTodos] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const queryClient = useQueryClient();

  const { data: users, isLoading: usersLoading } = useQuery(
    ["all-users"],
    async () => {
      const res = await getAdmins();
      return res;
    },
    { refetchOnWindowFocus: false }
  );

  const { data, isLoading, error } = useQuery(
    ["todos"],
    async () => {
      if (!token) {
        throw new Error("No token available");
      }
      try {
        const res = await api.get(`/todos`, {
          headers: { authorization: `Bearer ${token}` },
        });
        return res.data;
      } catch (error) {
        if (
          error?.response?.status === 401 &&
          error?.response?.data?.error === "You are not authorized to make this request"
        ) {
          window.location.reload();
          return null;
        }
        throw error;
      }
    },
    {
      enabled: !!token && isAuthenticated,
      retry: 3,
      retryDelay: 1000,
      onSuccess: (fetchedData) => {
        if (Array.isArray(fetchedData)) {
          const sortedData = sortTodosByDate(fetchedData);
          setLocalTodos(sortedData);
        } else {
          console.warn("Fetched data is not iterable:", fetchedData);
        }
        setIsInitialLoading(false); // Set to false when data is loaded
      },
      onError: (error) => {
        console.error("Error fetching todos:", error);
        console.error("Error details:", error.response?.data);
        toast.error("Failed to load todos. Please try again.");

      },
    }
  );


  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return { borderColor: "#FFC93F", backgroundColor: "#FFC93F" };
      case "In Progress":
        return { borderColor: "#7CE13E", backgroundColor: "#7CE13E" };
      case "completed":
        return { borderColor: "green", backgroundColor: "green" };
      default:
        return { borderColor: "#000", backgroundColor: "#fff" };
    }
  };

  const taskStatus = ["Pending", "In Progress", "Completed"];

  const updateStatusMutation = useMutation(
    ["task"],
    async (data) => {
      await api.put(`/todos/status/${data?.id}/${data?.status}`, {}, {
        headers: { authorization: `Bearer ${token}` },
      });
    },
    {
      onMutate: (variables) => {
        const previousTodos = queryClient.getQueryData(["todos"]);
        queryClient.setQueryData(["todos"], oldData => {
          const updatedData = oldData.map(todo =>
            todo._id === variables.id ? { ...todo, status: variables.status } : todo
          );
          return sortTodosByDate(updatedData);
        });
        return { previousTodos };
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["todos"]); // Invalidate admin todos
        queryClient.invalidateQueries(["personal-todos"]); // Invalidate the specific user's todos
        queryClient.invalidateQueries(["assigned-todos"]); // Invalidate the specific user's todos
        toast.success("Status updated!");
      },
      onError: (err, variables, context) => {
        queryClient.setQueryData(["todos"], context.previousTodos);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["todos"]);
      },
    }
  );

  const handleStatusChange = async (id, status) => {
    updateStatusMutation.mutate({ id, status });
      if (status === "completed") {
            try {
                  await api.post("/notifications", {
                  userId: user?.uid,
                  title: "Task Completed",
                  message: `The task with ${user.name} has been marked as completed.`,
                  linkText: "View Todo",
                  link: `/todo`,
                  role: "admin",
                  type: "task-completed",
                  });
                  toast.success("Notification sent for task completion!");
                } catch (error) {
                  console.error("Error sending notification:", error);
                  toast.error("Failed to send notification. Please try again.");
                }
              }
            };

  const sortTodosByDate = (todos) => {
    return [...todos].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA; // This will sort from newest to oldest
    });
  };

  const deleteTodoMutation = useMutation(
    ["todo-delete"],
    async (id) => {
      const response = await api.delete(`/todos/${id}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["todos"]);
        toast.success(data.message || "Todo deleted!");
      },
      onError: (error) => {
        console.error("Error deleting todo:", error);
        const errorMessage = error.response?.data?.error || error.message || "An unknown error occurred";
        console.error("Error details:", error.response?.data);
        toast.error(`Failed to delete todo: ${errorMessage}`);
      },
    }
  );

  const handleDelete = (record) => {
    // Check if user is super admin
    if (user.role === 'super admin') {
      // If super admin, delete without additional checks
      Modal.confirm({
        title: "Are you sure you want to delete this todo?",
        onOk: async () => {
          await deleteTodoMutation.mutate(record._id);
        },
      });
    } else {
      // For other users, check if they have permission
      if (record.userId === user.uid || record.assignedTo === user.uid || record.assignedBy === user.uid) {
        deleteTodoMutation.mutate(record._id);
      } else {
        toast.error("You don't have permission to delete this todo");
      }
    }
  };

  if (!isAuthenticated) {
    return <div>Loading...</div>;
  }

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      render: (_, record) => dayjs(record?.date).format("DD MMM YYYY"),
      sorter: false,
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      render: (_, record) => dayjs(record?.dueDate).format("DD MMM YYYY"),
    },
    {
      title: "Assigned To",
      dataIndex: "assignedTo",
      render: (_, record) =>
        record?.assignedTo
          ? users?.find((e) => e?.id === record?.assignedTo)?.name
          : "-",
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (_, record) => (record?.title ? record?.title : "-"),
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => (
        // <Tag
        //   className={`capitalize text-white border-none px-2 w-fit flex items-center justify-center rounded-full ${
        //     record?.status === "open" ? "bg-green-500" : "bg-red-500"
        //   }`}
        // >
        //   {record?.status}
        // </Tag>
        // <select
        //   className={`border capitalize rounded-sm text-white p-1 py-1  bg-[#e5edfb] w-48`}
        //   style={{
        //     ...getStatusStyles(record?.status),
        //   }}
        //   value={record?.status}
        //   onChange={(e) => handleStatusChange(record?._id, e.target.value)}
        // >
        //   <option value="" disabled>
        //     Select Status
        //   </option>
        //   {taskStatus?.map((status, index) => (
        //     <option key={index} value={status}>
        //       {status}
        //     </option>
        //   ))}
        // </select>
        <StatusDropdown status={record?.status} onChange={(id, status) => handleStatusChange(id, status)} id={record?._id} loading={updateStatusMutation.isLoading} />
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      render: (_, record) => (
        <Tag
          className={`capitalize text-white border-none px-2 w-fit flex items-center justify-center rounded-full ${record?.priority === "Normal"
            ? "bg-green-500"
            : record?.priority === "Medium"
              ? "bg-yellow-500"
              : record?.priority === "High"
                ? "bg-red-500"
                : "bg-red-900"
            }`}
        >
          {record?.priority}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Dropdown
          placement="bottomRight"
          overlay={
            <Menu>
              <Menu.Item
                onClick={() => handleDelete(record)}
                key="delete"
              >
                <Space>Delete</Space>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <a
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Space>
              <button className="border relative p-1 rounded-md border-[#053F92]">
                <PiDotsThreeVerticalBold size={20} />
              </button>
            </Space>
          </a>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="flex flex-col">
      {isLoading || usersLoading || isInitialLoading ? (
        <Loader />
      ) : localTodos.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No todos.
        </div>
      ) : (
        <Table columns={columns} dataSource={localTodos} loading={isLoading} />
      )}
    </div>
  );
};

export default SuperAdminTodos;